/* eslint-disable canvas/no-swallow-errors-in-catch */
// dependency-graph-ignore-file
import '@atlaskit/css-reset';
import { logFatalError } from '@databases/logs';
import React from 'react';
import { createRoot } from 'react-dom/client';

import '@databases/app-access/init';
import '../store-tracking.ts';

import { initializeBootstrapping } from '../bootstrap/bootstrap.ts';
import { getRootAppElement } from '../bootstrap/elements.ts';
import { handleFatalErrorLogging } from '../bootstrap/handle-fatal-error-logging.ts';
import { handleFatalErrorUI } from '../bootstrap/handle-fatal-error-ui.ts';
import { mountAppWithMetrics } from '../bootstrap/mount-app-with-metrics.tsx';
import { mountSkeleton } from '../bootstrap/mount-skeleton.ts';

import { ErrorApp } from './components/error-app.tsx';

interface ErrorBoundAppSetupParams {
  bootstrap: ReturnType<typeof initializeBootstrapping>;
  mount: typeof mountAppWithMetrics;
}

/**
 * Establishes an error boundary around the application's bootstrapping process.
 * This function enables injection of a custom setup routine, providing it with both
 * the `bootstrap` context and `mount` methods. Intended for use by entry points to
 * ensure consistent initialization and error handling across the application.
 *
 * ```
 * const app = createDatabasesApp(({ bootstrap, mount }) => {
 *    await bootstrap({
 *      cloudId,
 *      dependencies: {
 *        SomeDependency: () => new SomeImplementation(),
 *      },
 *      spaceId,
 *      userId,
 *    });
 *
 *    mount((getRoot) => {
 *      const root = getRoot();
 *
 *      root.render(<App />);
 *    });
 * });
 *
 * app();
 * ```
 */
export function createDatabasesApp(
  setupApp: (params: ErrorBoundAppSetupParams) => Promise<void>,
): () => Promise<void> {
  return async function wrappedErrorBoundary() {
    try {
      const bootstrap = initializeBootstrapping();
      const unmountSkeleton = mountSkeleton();

      await setupApp({
        bootstrap,
        mount: (rootGetter: Parameters<typeof mountAppWithMetrics>[0]) => {
          mountAppWithMetrics(rootGetter);

          requestAnimationFrame(unmountSkeleton);
        },
      });
    } catch (error) {
      handleFatalErrorLogging(error);

      if (error instanceof Error) {
        try {
          const rootElement = getRootAppElement();

          createRoot(rootElement).render(<ErrorApp error={error} />);

          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch (error2) {
          if (error2 instanceof Error) {
            logFatalError(error2);

            handleFatalErrorUI(error2);
          }
        }
      }
    }
  };
}
